'use client';

import { MapIcon } from '@/components/icons/map-icon';
import { TheaterStatus } from '@/enums/theater-status';
import { useFeatureFlags } from '@/hooks/state/feature-flags';
import { cn } from '@/lib/utils';
import { formatTheaterAddress } from '@/utils/address-helper';
import { formatOpeningDate } from '@/utils/format-helpers';
import { isTheaterTemporarilyClosed } from '@/utils/theaters/theater-status';
import { Theater } from '@cinemark/content-api-client';
import clsx from 'clsx';
import Image from 'next/image';
import { CinemarkLink } from '../cinemark-link/cinemark-link';
import { TheaterPlaceholderIcon } from '../icons/theater-placeholder-icon';
import { TheaterAmenityComponent, TheaterAmenitySize } from '../theater-amenity/theater-amenity';
import { Card, CardContent, CardHeader } from '../ui/card';
import { TheaterTitleComponent } from './theater-title/theater-title';
import theaterStyles from './theater.module.scss';

type TheaterComponentProps = {
  amenitySize?: TheaterAmenitySize;
  className?: string;
  dataLinkCategory: string;
  hideTheaterExteriorImage?: boolean;
  maxAmenitiesToShow?: number;
  theater: Theater;
};

export const TheaterComponent = ({
  amenitySize,
  className,
  dataLinkCategory,
  hideTheaterExteriorImage = false,
  maxAmenitiesToShow = 4,
  theater,
}: TheaterComponentProps): React.JSX.Element => {
  const { data: featureFlags } = useFeatureFlags();
  const maxAmenities = theater.attributes?.slice(0, maxAmenitiesToShow) || [];
  const exteriorImage = theater.exteriorPictureLarge || theater.exteriorPictureMedium || theater.exteriorPictureSmall;
  const isTemporarilyClosed = isTheaterTemporarilyClosed(theater.status);
  const isClosed = theater.status === TheaterStatus.ClosedPermanently || isTemporarilyClosed;
  const useStatusMessage = isClosed || theater.status === TheaterStatus.OpeningSoon;
  const showTheaterHeader = featureFlags?.theaters.enableTheaterImage && !hideTheaterExteriorImage;
  const showExteriorImage = showTheaterHeader && !!exteriorImage?.url;

  return (
    <Card className={clsx('w-full', className)}
      fullHeader={showTheaterHeader}
    >
      {showTheaterHeader &&
        <CardHeader>
          <CinemarkLink useAnchor
            className="h-37 md:h-50"
            dataLinkCategory={dataLinkCategory}
            dataLinkName="Theatre Click"
            dataTest={`link-theater-${theater.theaterId}`}
            href={`/theatre-${theater.theaterId}`}
          >
            {showExteriorImage &&
              <Image alt={exteriorImage.filename!}
                aria-label={`${theater.title} exterior image`}
                className="w-full h-full rounded-t-md object-cover object-center"
                height={exteriorImage.height}
                src={exteriorImage.url!}
                width={exteriorImage.width}
              />
            }

            {!showExteriorImage &&
              <div className="w-full h-full flex items-center justify-center">
                <TheaterPlaceholderIcon className="w-15 h-15" />
              </div>
            }
          </CinemarkLink>
        </CardHeader>
      }

      <CardContent className={clsx('overflow-hidden', theaterStyles.theater)}
        fullHeader={showTheaterHeader}
      >
        <div className="flex flex-col gap-sm">
          <TheaterTitleComponent dataLinkCategory={dataLinkCategory}
            theater={theater}
          />

          <div className="flex items-center gap-sm">
            <CinemarkLink primary
              className="inline-flex items-center gap-sm"
              dataLinkCategory={dataLinkCategory}
              dataLinkName="Theatre Directions"
              dataTest="link-theater-directions"
              href={theater.mapUrl || '#'}
              target="_blank"
            >
              <MapIcon aria-hidden="true"
                className="w-[16px] h-[16px]"
              />
            </CinemarkLink>
            <p className="text-sm"
              data-test="theater-address"
            >
              {formatTheaterAddress(theater)}
            </p>
          </div>
        </div>

        {useStatusMessage && theater.status === TheaterStatus.OpeningSoon &&
          <div className="flex items-center justify-center gap-xs w-full">
            <p className="font-bold"
              data-test={`theater-status-${theater.theaterId}`}
            >
              Theatre Opens {theater.openingSoonDate ? formatOpeningDate(theater.openingSoonDate) : 'Soon'}!
            </p>
            <CinemarkLink primary
              dataLinkCategory={dataLinkCategory}
              dataLinkName={`Theatre ${theater.theaterId} Learn More`}
              dataTest={`link-learn-more-theater-${theater.theaterId}`}
              href={`/theatre-${theater.theaterId}`}
            >
              Learn More
            </CinemarkLink>
          </div>
        }

        {useStatusMessage && isClosed &&
          <p className="font-bold text-center"
            data-test={`theater-status-${theater.theaterId}`}
          >
            {isTemporarilyClosed ? 'Temporarily Closed' : theater.status}
          </p>
        }

        {!isClosed && !!maxAmenities.length &&
          <div className="flex flex-col gap-sm">
            <h5 data-test="theater-amenities">Theatre Amenities</h5>
            <div className={cn(theaterStyles.amenities, 'items-start')}>
              {maxAmenities.map(amenity =>
                <TheaterAmenityComponent key={amenity.id}
                  attribute={amenity}
                  className="max-w-[min(100px,25%)] w-full"
                  dataLinkCategory={dataLinkCategory}
                  size={amenitySize}
                  textClassName="text-xs"
                  theater={theater}
                />
              )}
            </div>
          </div>
        }
      </CardContent>
    </Card>
  );
};
